import React, { useState } from "react";
import { Smartphone, Building2 } from "lucide-react";
import { toast } from "react-toastify";
import { triggerSecurityPanicIncident } from "../../../service/securityService";

const SecurityPanicTriggers: React.FC<{ onCreated: () => void }> = ({ onCreated }) => {
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState<false | "mobile" | "wall">(false);

  const fire = async (panicSource: "mobile" | "wall_mounted") => {
    setBusy(panicSource === "wall_mounted" ? "wall" : "mobile");
    try {
      await triggerSecurityPanicIncident({
        panicSource,
        location: location.trim() || undefined,
        description: description.trim() || undefined,
      });
      toast.success("Panic incident raised — security register updated");
      setDescription("");
      onCreated();
    } catch (e) {
      console.error(e);
      toast.error("Failed to raise panic incident");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-red-900">Panic triggers</h3>
        <p className="text-xs text-red-800/90 mt-1">
          Simulates mobile app and wall-mounted duress buttons. Creates a Critical security incident
          with auto-escalation labels (evidence not required). Physical devices should call the
          authenticated security mobile panic endpoint with optional source and location metadata.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Location hint (optional)</label>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="e.g. Ward 3 nurse station"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Note (optional)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Short context for responders"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!!busy}
          onClick={() => fire("mobile")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
        >
          <Smartphone className="w-4 h-4" />
          {busy === "mobile" ? "Sending…" : "Mobile panic"}
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => fire("wall_mounted")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-800 text-white text-sm font-semibold hover:bg-red-900 disabled:opacity-50"
        >
          <Building2 className="w-4 h-4" />
          {busy === "wall" ? "Sending…" : "Wall-mounted panic"}
        </button>
      </div>
    </div>
  );
};

export default SecurityPanicTriggers;
